import dayjs from 'dayjs';
import { JSONSchema7 } from 'json-schema';

import { Categories, Category, Assets, Asset, EditAssets, Properties } from '../data';
// Load types and schema from OpenAPI. See project README.md for info on how these files are formed.
import { components } from '../../build/openapiTypes';
import schema from '../../build/openapiJsonSchema.json';
export interface Indexable<T> {
    [key: string]: T;
}

export enum UiSchemaKey {
    Widget = 'x-uischema-widget',
    WidgetProps = 'x-uischema-widget-props',
    WidgetDetail = 'x-uischema-widget-detail',
    WidgetDetailProps = 'x-uischema-widget-detail-props',
    WidgetTable = 'x-uischema-widget-table',
    WidgetTableProps = 'x-uischema-widget-table-props',
    Help = 'x-uischema-help',
    Rows = 'x-uischema-rows',
}

export type WidgetUiSchemaKey =
    | UiSchemaKey.Widget
    | UiSchemaKey.WidgetDetail
    | UiSchemaKey.WidgetTable
    | undefined;

export enum UiKey {
    Description = 'ui:description',
    Help = 'ui:help',
    Placeholder = 'ui:placeholder',
    Rows = 'ui:rows',
    Widget = 'ui:widget',
}

type Filterable = string | number | boolean;

export const yesNoFilter = (columnName: string) => (value: Filterable, record: any) =>
    (record[columnName] ? 'yes' : 'no').includes(value.toString().toLowerCase());

export const defaultDateDisplay = 'MMM D YYYY';
export const defaultDateTimeDisplay = 'MMM D YYYY h:mm A';

export const displayDate = (
    date: string,
    inputFormat?: string,
    displayFormat: string = defaultDateDisplay
) => {
    return dayjs(date, inputFormat).format(displayFormat);
};

export const tableWideFilter = (searchText: string) => (data: { [key: string]: any }) =>
    Object.values(data).some((val: any) => {
        if (Array.isArray(val)) {
            return val.some((arrayVal) =>
                arrayVal.toLowerCase()?.includes(searchText.toLowerCase())
            );
        } else {
            if (val && !val.toString) {
                console.error('cant search on this value:', val);
            }
            return val?.toString().toLowerCase()?.includes(searchText.toLowerCase());
        }
    });

export interface CategoryAndAssetDetails {
    categoryData: Category;
    assetData: Asset;
    assetSchema: JSONSchema7;
    assetSchemaProperties: Indexable<Properties>;
    assetNameFromType: keyof components['schemas'];
}

export const getCategoryAndAssetDetails = (
    category: string,
    isEdit: boolean = false
): CategoryAndAssetDetails => {
    const categoryData = Categories[category];
    if (!categoryData) {
        throw new Error(`'${category}' is an unknown category`);
    }
    const assetName = isEdit ? categoryData.editAsset : categoryData.asset;
    const assetData = isEdit ? EditAssets[assetName] : Assets[assetName];

    // get asset schema for use building the dynamic form
    const assetNameFromJson = assetName as keyof typeof schema.definitions;
    const assetSchema = schema.definitions[assetNameFromJson] as JSONSchema7;
    const assetSchemaProperties = assetSchema.properties as Indexable<Properties>;

    // grab ts type for the asset
    // omitting string because we want only objects
    const assetNameFromType = assetName as keyof components['schemas'];

    return { categoryData, assetData, assetSchema, assetSchemaProperties, assetNameFromType };
};
